import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectorRef
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ConfigService } from 'src/app/services/config.service';
import { SettingsService } from 'src/app/services/settings.service';

declare var THREE: any;

interface GraphNode {
  id: string;
  type: string;
  label: string;
  count: number;
  messages: { ref: string; excerpt: string; message_id?: string }[];
}

interface GraphEdge {
  source: string;
  target: string;
  weight: number;
}

interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

const TYPE_COLORS_HEX: Record<string, number> = {
  concepts: 0x7aa2f7,
  products: 0xf7768e,
  people:   0x9ece6a,
  patents:  0xe0af68,
  'file:':  0xbb9af7,
  tickets:  0xff9e64,
  channels: 0x73daca,
  backlog:  0xffc777,
  deferred: 0xffc777,
  other:    0x565f89,
};

@Component({
  selector: 'app-knowledge-graph-widget',
  templateUrl: './knowledge-graph-widget.component.html',
  styleUrls: ['../base-widget/base-widget.component.scss', './knowledge-graph-widget.component.scss'],
  standalone: false
})
export class KnowledgeGraphWidgetComponent extends BaseWidgetComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('graphContainer') containerRef: ElementRef<HTMLDivElement>;
  @ViewChild('graphCanvas') canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('threatCanvas') threatCanvasRef: ElementRef<HTMLCanvasElement>;
  @Input() channel: string | null = null;
  @Input() embedded: boolean = false;
  @Input() team: string = 'local';
  @Output() nodeSelected = new EventEmitter<GraphNode>();

  loading = true;
  error: string | null = null;
  selectedNode: GraphNode | null = null;
  messagePreview: { ref: string; content: string; loading: boolean } | null = null;
  nodeCount = 0;
  edgeCount = 0;
  visibleTypes: string[] = [];

  // Type filter state
  activeTypeFilter: string | null = null;

  // Hover tooltip state
  hoveredNode: GraphNode | null = null;
  hoverTooltipX = 0;
  hoverTooltipY = 0;

  // Search state
  searchQuery = '';
  searchResults: GraphNode[] = [];
  searchOpen = false;
  searchActiveIndex = 0;

  typeColors: Record<string, string> = {
    concepts: '#7aa2f7', products: '#f7768e', people: '#9ece6a',
    patents: '#e0af68', 'file:': '#bb9af7', tickets: '#ff9e64',
    channels: '#73daca', backlog: '#ffc777', deferred: '#ffc777', other: '#565f89'
  };
  private readonly typeBgDark: Record<string, string> = {
    concepts: '#1a2744', products: '#2d1520', people: '#1a2d1a',
    patents: '#2d2510', 'file:': '#251a2d', tickets: '#2d1f10',
    channels: '#1a2d2d', backlog: '#2d2510', deferred: '#2d2510', other: '#1a1a2d'
  };
  private readonly typeBgLight: Record<string, string> = {
    concepts: '#e0ecff', products: '#ffe0e6', people: '#e0f5e0',
    patents: '#fff3d0', 'file:': '#f0e0ff', tickets: '#ffe8d0',
    channels: '#d8f5f0', backlog: '#fff3d0', deferred: '#fff3d0', other: '#e8e8f0'
  };
  get typeBg(): Record<string, string> {
    return this.isDark ? this.typeBgDark : this.typeBgLight;
  }
  typeLabels: Record<string, string> = {
    concepts: 'Concepts', products: 'Products', people: 'People',
    patents: 'Patents', 'file:': 'Files', tickets: 'Tickets',
    channels: 'Channels', backlog: 'Backlog', deferred: 'Backlog', other: 'Other'
  };

  private scene: any;
  private camera: any;
  private renderer: any;
  private controls: any;
  private nodeMeshes: any[] = [];
  private glowMeshes: any[] = [];
  private labelSprites: any[] = [];
  private animId: number | null = null;
  private resizeObs: ResizeObserver | null = null;
  private threeLoaded = false;

  // Fly mode state
  flyMode = false;
  flySpeed = 0;
  private flyVelocity: any = null; // THREE.Vector3
  flyKeys: Record<string, boolean> = {};
  readonly FLY_ACCEL = 0.35;
  readonly FLY_BOOST_MULT = 2.5;
  readonly FLY_BRAKE_MULT = 0.80;
  readonly FLY_DAMPING = 0.93;
  readonly FLY_MAX_SPEED = 2.0;
  private readonly PROXIMITY_RADIUS = 20;

  // Combat state
  combatMode = false;
  score = 0;
  nodesInfected = 0;
  nodesRemaining = 0;
  private infectedNodes: boolean[] = []; // track which nodes are infected
  private projectiles: { mesh: any; velocity: any; age: number }[] = [];
  private baddies: { mesh: any; targetIdx: number; speed: number; hp: number }[] = [];
  private explosions: { particles: any; age: number; group: any }[] = [];
  private baddieMaterial: any = null;
  private projectileGeo: any = null;
  private projectileMat: any = null;
  private lastBaddieSpawn = 0;
  private readonly BADDIE_SPAWN_INTERVAL = 3.5; // seconds
  private readonly MAX_BADDIES = 12;
  private readonly PROJECTILE_SPEED = 3.0;
  private readonly PROJECTILE_LIFETIME = 120; // frames
  private readonly BADDIE_HIT_RADIUS = 2.5;
  private readonly NODE_HIT_RADIUS = 3.0;
  // Powerup state
  private powerups: { mesh: any; orbitAngle: number; orbitRadius: number; orbitSpeed: number; orbitY: number }[] = [];
  private lastPowerupSpawn = 0;
  private readonly POWERUP_SPAWN_INTERVAL = 12.0; // seconds
  private readonly MAX_POWERUPS = 3;
  private readonly POWERUP_HIT_RADIUS = 3.0;
  spreadFireActive = false;
  spreadFireTimer = 0;
  private readonly SPREAD_FIRE_DURATION = 10.0; // seconds
  // Threat indicator state (rendered in canvas overlay)
  private threatIndicatorCtx: CanvasRenderingContext2D | null = null;
  private threatCanvas: HTMLCanvasElement | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private keyupHandler: ((e: KeyboardEvent) => void) | null = null;
  private mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
  private pointerLockHandler: (() => void) | null = null;
  private canvasClickHandler: ((e: MouseEvent) => void) | null = null;
  private canvasHoverHandler: ((e: MouseEvent) => void) | null = null;
  private flyEuler: any = null; // THREE.Euler for mouse look
  private savedCameraPos: any = null; // saved orbit camera position
  private savedTarget: any = null; // saved orbit target
  private defaultCameraZ = 140; // computed from bounding sphere after layout

  get isDark(): boolean {
    return this._settings.theme === 'dark';
  }

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private _settings: SettingsService,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.loadThreeJs().then(() => this.loadGraph());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channel'] && !changes['channel'].firstChange && this.threeLoaded) {
      this.teardownScene();
      this.loadGraph();
    }
  }

  async reloadGraph(): Promise<void> {
    if (this.loading || !this.threeLoaded) return;
    this.teardownScene();
    await this.loadGraph();
  }

  private loadThreeJs(): Promise<void> {
    if (typeof THREE !== 'undefined' && THREE.OrbitControls) {
      this.threeLoaded = true;
      return Promise.resolve();
    }
    return this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js')
      .then(() => this.loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js'))
      .then(() => { this.threeLoaded = true; });
  }

  private loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) { resolve(); return; }
      const s = document.createElement('script');
      s.src = url;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.head.appendChild(s);
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.teardownScene();
  }

  private async loadGraph(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.selectedNode = null;
    this.nodeCount = 0;
    this.edgeCount = 0;
    this.visibleTypes = [];
    this.cdr.detectChanges();

    try {
      let url = `${ConfigService.muxUrl}/knowledge-graph?team=${encodeURIComponent(this.team)}`;
      if (this.channel) url += `&channel=${encodeURIComponent(this.channel)}`;

      const data = await this.http.get<GraphData>(url).toPromise();
      if (!data || data.nodes.length === 0) {
        this.loading = false;
        this.error = 'No wikilinks found yet. Links appear as messages are tagged with LINKS: footers.';
        this.cdr.detectChanges();
        return;
      }

      this.loading = false;
      this.cdr.detectChanges();

      // Wait a tick for canvas to render
      setTimeout(() => this.buildScene(data), 0);
    } catch (e: any) {
      this.loading = false;
      this.error = `Failed to load graph: ${e.message || e}`;
      this.cdr.detectChanges();
    }
  }

  private buildScene(data: GraphData): void {
    const { nodes, edges } = this.processData(data);
    this.nodeCount = nodes.length;
    this.edgeCount = edges.length;
    this.visibleTypes = [...new Set(nodes.map(n => n.type))];
    this.cdr.detectChanges();

    const container = this.containerRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;

    // Scene
    this.scene = new THREE.Scene();

    // Camera
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // Controls
    this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.5;

    // Force layout
    const nodeMap: Record<string, number> = {};
    nodes.forEach((n, i) => nodeMap[n.id] = i);
    const positions = this.forceLayout(nodes, edges, nodeMap);

    // Compute camera distance from bounding sphere — use tighter of vertical/horizontal FOV
    const maxR = positions.reduce((r, p) => Math.max(r, p.length()), 0);
    const vFov = (this.camera.fov * Math.PI) / 180;
    const aspect = this.camera.aspect;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    const effectiveFov = Math.min(vFov, hFov);
    this.defaultCameraZ = (maxR / Math.sin(effectiveFov / 2)) * 1.15;
    this.camera.position.set(0, 0, this.defaultCameraZ);

    // Node meshes
    const nodeGroup = new THREE.Group();
    nodes.forEach((n, i) => {
      const size = 0.6 + Math.sqrt(n.count) * 0.6;
      const color = TYPE_COLORS_HEX[n.type] || TYPE_COLORS_HEX.other;

      const geo = new THREE.SphereGeometry(size, 24, 24);
      const mat = new THREE.MeshPhongMaterial({
        color, emissive: color, emissiveIntensity: 0.3,
        transparent: true, opacity: 0.9
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(positions[i]);
      mesh.userData = { nodeIndex: i, node: n };
      nodeGroup.add(mesh);
      this.nodeMeshes.push(mesh);

      // Glow
      const glowGeo = new THREE.SphereGeometry(size * 1.8, 16, 16);
      const glowMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.08 });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      glow.position.copy(positions[i]);
      glow.userData = { baseOpacity: 0.08 };
      nodeGroup.add(glow);
      this.glowMeshes.push(glow);
    });
    this.scene.add(nodeGroup);

    // Edges
    const edgeColor = this.isDark ? 0x3d4f7a : 0xb0b8d0;
    const edgeGroup = new THREE.Group();
    edges.forEach(e => {
      const si = nodeMap[e.source], ti = nodeMap[e.target];
      if (si === undefined || ti === undefined) return;
      const geo = new THREE.BufferGeometry().setFromPoints([positions[si], positions[ti]]);
      const opacity = Math.min(0.15 + e.weight * 0.08, 0.5);
      const mat = new THREE.LineBasicMaterial({ color: edgeColor, transparent: true, opacity });
      edgeGroup.add(new THREE.Line(geo, mat));
    });
    this.scene.add(edgeGroup);

    // Labels
    const labelColor = this.isDark ? '#c0caf5' : '#1a1a2e';
    const labelGroup = new THREE.Group();
    nodes.forEach((n, i) => {
      const cnv = document.createElement('canvas');
      const ctx = cnv.getContext('2d')!;
      const fontSize = 28;
      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
      const tw = ctx.measureText(n.label).width + 20;
      cnv.width = tw;
      cnv.height = fontSize + 12;
      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillStyle = labelColor;
      ctx.textBaseline = 'middle';
      ctx.fillText(n.label, 10, cnv.height / 2);

      const tex = new THREE.CanvasTexture(cnv);
      const spriteMat = new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.85 });
      const sprite = new THREE.Sprite(spriteMat);
      const size = 0.6 + Math.sqrt(n.count) * 0.6;
      sprite.position.copy(positions[i]).add(new THREE.Vector3(0, size + 1.5, 0));
      sprite.scale.set(tw / 40, (fontSize + 12) / 40, 1);
      sprite.userData = { baseOpacity: 0.85 };
      labelGroup.add(sprite);
      this.labelSprites.push(sprite);
    });
    this.scene.add(labelGroup);

    // Lighting — brighter ambient in light mode so nodes stay vivid
    this.scene.add(new THREE.AmbientLight(this.isDark ? 0x404060 : 0xd0d0e0, this.isDark ? 0.5 : 0.8));
    const dirLight = new THREE.DirectionalLight(0x7aa2f7, this.isDark ? 0.8 : 0.5);
    dirLight.position.set(50, 50, 50);
    this.scene.add(dirLight);
    const pointLight = new THREE.PointLight(0xf7768e, this.isDark ? 0.4 : 0.25, 200);
    pointLight.position.set(-30, -30, 30);
    this.scene.add(pointLight);

    // Particles
    const particleColor = this.isDark ? 0x3d4f7a : 0xb0b8d0;
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(200 * 3);
    for (let i = 0; i < 600; i++) pPos[i] = (Math.random() - 0.5) * 150;
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    this.scene.add(new THREE.Points(pGeo, new THREE.PointsMaterial({
      color: particleColor, size: 0.15, transparent: true, opacity: 0.4
    })));

    // Raycaster for clicks
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    this.canvasClickHandler = (event: MouseEvent) => {
      // In fly mode, click shoots or re-locks pointer
      if (this.flyMode) {
        if (document.pointerLockElement !== this.renderer.domElement) {
          this.renderer.domElement.requestPointerLock();
        } else if (this.combatMode) {
          this.shoot();
        }
        return;
      }
      const rect = this.renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, this.camera);
      const intersects = raycaster.intersectObjects(this.nodeMeshes);
      if (intersects.length > 0) {
        const node = intersects[0].object.userData['node'];
        if (this.embedded) {
          this.nodeSelected.emit(node);
        } else {
          this.selectedNode = node;
        }
        this.controls.autoRotate = false;
      } else {
        this.selectedNode = null;
        this.controls.autoRotate = true;
      }
      this.cdr.detectChanges();
    };
    this.renderer.domElement.addEventListener('click', this.canvasClickHandler);
    // Hover cursor + tooltip
    let hoveredMesh: any = null;
    this.canvasHoverHandler = (event: MouseEvent) => {
      const rect = this.renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, this.camera);
      const intersects = raycaster.intersectObjects(this.nodeMeshes);
      if (hoveredMesh) {
        hoveredMesh.material.emissiveIntensity = 0.3;
        if (!this.flyMode) this.renderer.domElement.style.cursor = 'default';
      }
      if (intersects.length > 0) {
        hoveredMesh = intersects[0].object;
        hoveredMesh.material.emissiveIntensity = 0.8;
        if (!this.flyMode) this.renderer.domElement.style.cursor = 'pointer';
        const node = hoveredMesh.userData['node'] as GraphNode;
        this.hoveredNode = node;
        const containerRect = container.getBoundingClientRect();
        this.hoverTooltipX = event.clientX - containerRect.left + 12;
        this.hoverTooltipY = event.clientY - containerRect.top - 8;
        this.cdr.detectChanges();
      } else {
        hoveredMesh = null;
        if (this.hoveredNode) {
          this.hoveredNode = null;
          this.cdr.detectChanges();
        }
      }
    };
    this.renderer.domElement.addEventListener('mousemove', this.canvasHoverHandler);

    // Resize handling — re-fit camera so sphere stays fully visible
    this.resizeObs = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w && h) {
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h);
        if (!this.flyMode) {
          const vFov = (this.camera.fov * Math.PI) / 180;
          const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
          const effectiveFov = Math.min(vFov, hFov);
          this.defaultCameraZ = (maxR / Math.sin(effectiveFov / 2)) * 1.15;
          this.camera.position.setZ(this.defaultCameraZ);
          this.controls.target.set(0, 0, 0);
        }
      }
    });
    this.resizeObs.observe(container);

    // Fly mode keyboard controls
    this.flyVelocity = new THREE.Vector3();
    this.keydownHandler = (e: KeyboardEvent) => {
      // Don't capture when typing in inputs
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      this.flyKeys[key] = true;
      // Toggle fly mode with F
      if (key === 'f' && !e.repeat) {
        this.flyMode = !this.flyMode;
        if (this.flyMode) {
          // Save orbit state for clean restore
          this.savedCameraPos = this.camera.position.clone();
          this.savedTarget = this.controls.target.clone();
          // Init euler from current camera rotation
          this.flyEuler = new THREE.Euler(0, 0, 0, 'YXZ');
          this.flyEuler.setFromQuaternion(this.camera.quaternion);
          this.controls.enabled = false;
          this.controls.autoRotate = false;
          this.renderer.domElement.style.cursor = 'crosshair';
          // Request pointer lock for mouse look
          this.renderer.domElement.requestPointerLock();
        } else {
          // Exit combat if active
          if (this.combatMode) this.toggleCombat();
          // Exit pointer lock
          if (document.pointerLockElement) document.exitPointerLock();
          // Restore orbit state
          if (this.savedCameraPos && this.savedTarget) {
            this.camera.position.copy(this.savedCameraPos);
            this.controls.target.copy(this.savedTarget);
            this.camera.lookAt(this.savedTarget);
          }
          this.controls.enabled = true;
          this.flyVelocity.set(0, 0, 0);
          this.flySpeed = 0;
          this.flyKeys = {};
          this.renderer.domElement.style.cursor = 'default';
        }
        this.cdr.detectChanges();
      }
      // Toggle combat with G (only in fly mode)
      if (key === 'g' && !e.repeat && this.flyMode) {
        this.toggleCombat();
      }
      // Reset camera with R
      if (key === 'r' && !e.repeat) {
        this.camera.position.set(0, 0, this.defaultCameraZ);
        this.controls.target.set(0, 0, 0);
        this.camera.lookAt(0, 0, 0);
        if (this.flyMode) {
          this.flyVelocity.set(0, 0, 0);
          this.flySpeed = 0;
          this.flyEuler.set(0, 0, 0);
          this.camera.quaternion.setFromEuler(this.flyEuler);
        }
        this.savedCameraPos = this.camera.position.clone();
        this.savedTarget = this.controls.target.clone();
        this.controls.update();
        this.cdr.detectChanges();
      }
    };
    this.keyupHandler = (e: KeyboardEvent) => {
      this.flyKeys[e.key.toLowerCase()] = false;
    };
    window.addEventListener('keydown', this.keydownHandler);
    window.addEventListener('keyup', this.keyupHandler);

    // Mouse look handler (pointer lock)
    this.mouseMoveHandler = (e: MouseEvent) => {
      if (!this.flyMode || !this.flyEuler) return;
      if (document.pointerLockElement !== this.renderer.domElement) return;
      const sensitivity = 0.002;
      this.flyEuler.y -= e.movementX * sensitivity;
      this.flyEuler.x -= e.movementY * sensitivity;
      // Clamp pitch to avoid flipping
      this.flyEuler.x = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, this.flyEuler.x));
      this.camera.quaternion.setFromEuler(this.flyEuler);
    };
    document.addEventListener('mousemove', this.mouseMoveHandler);

    // Pointer lock change handler
    this.pointerLockHandler = () => {
      if (document.pointerLockElement !== this.renderer.domElement && this.flyMode) {
        // Pointer lock lost (ESC, PrtSc, alt-tab, etc.)
        // Don't exit fly/combat — just show cursor so user can click to re-lock
        this.renderer.domElement.style.cursor = 'crosshair';
        this.cdr.detectChanges();
      }
    };
    document.addEventListener('pointerlockchange', this.pointerLockHandler);

    // Animate
    const animate = () => {
      this.animId = requestAnimationFrame(animate);
      const t = Date.now() * 0.001;

      if (this.flyMode) {
        // Build movement vector from WASD + QE (up/down)
        const move = new THREE.Vector3();
        if (this.flyKeys['w']) move.z -= 1;
        if (this.flyKeys['s']) move.z += 1;
        if (this.flyKeys['a']) move.x -= 1;
        if (this.flyKeys['d']) move.x += 1;
        if (this.flyKeys['q'] || this.flyKeys['e']) {
          move.y += this.flyKeys['e'] ? 1 : -1;
        }

        // Transform to camera space
        if (move.lengthSq() > 0) {
          move.normalize();
          move.applyQuaternion(this.camera.quaternion);
          const boost = this.flyKeys[' '] ? this.FLY_BOOST_MULT : 1;
          this.flyVelocity.add(move.multiplyScalar(this.FLY_ACCEL * boost));
        }

        // Brake with shift
        if (this.flyKeys['shift']) {
          this.flyVelocity.multiplyScalar(this.FLY_BRAKE_MULT);
        }

        // Clamp speed
        const maxSpd = this.flyKeys[' '] ? this.FLY_MAX_SPEED * this.FLY_BOOST_MULT : this.FLY_MAX_SPEED;
        if (this.flyVelocity.length() > maxSpd) {
          this.flyVelocity.normalize().multiplyScalar(maxSpd);
        }

        // Apply damping + move
        this.flyVelocity.multiplyScalar(this.FLY_DAMPING);
        this.camera.position.add(this.flyVelocity);
        this.flySpeed = this.flyVelocity.length();

        // Keep orbit controls target in front of camera (so controls.update doesn't fight)
        const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
        this.controls.target.copy(this.camera.position).add(fwd.multiplyScalar(10));
      }

      this.controls.update();

      // Proximity glow — nodes brighten as camera approaches
      const camPos = this.camera.position;
      this.nodeMeshes.forEach((m, i) => {
        const dist = camPos.distanceTo(m.position);
        const proximity = Math.max(0, 1 - dist / this.PROXIMITY_RADIUS);

        // Infected nodes pulse red aggressively
        if (this.combatMode && this.infectedNodes[i]) {
          const infPulse = 1.1 + Math.sin(t * 3 + i) * 0.15;
          m.scale.setScalar(infPulse);
          m.material.emissiveIntensity = 0.6 + Math.sin(t * 4 + i) * 0.3;
          if (this.glowMeshes[i]) {
            this.glowMeshes[i].material.opacity = 0.2 + Math.sin(t * 3 + i) * 0.1;
            this.glowMeshes[i].scale.setScalar(1.8);
          }
        } else {
          // Normal pulse + proximity
          const pulse = 1 + Math.sin(t * 0.5 + i) * 0.03;
          m.scale.setScalar(pulse + proximity * 0.3);
          m.material.emissiveIntensity = 0.3 + proximity * 0.7;
          if (this.glowMeshes[i]) {
            this.glowMeshes[i].material.opacity = 0.08 + proximity * 0.25;
            this.glowMeshes[i].scale.setScalar(1 + proximity * 0.5);
          }
        }
        // Labels brighten on proximity
        if (this.labelSprites[i]) {
          this.labelSprites[i].material.opacity = 0.4 + proximity * 0.6;
        }
      });

      // Combat update
      this.updateCombat(t);

      this.renderer.render(this.scene, this.camera);

      // Update speed/combat display (throttle to ~10fps)
      if (this.flyMode && Math.floor(t * 10) !== Math.floor((t - 0.016) * 10)) {
        this.cdr.detectChanges();
      }
    };
    animate();
  }

  private processData(data: GraphData): { nodes: GraphNode[]; edges: GraphEdge[] } {
    const seenLabels = new Set<string>();
    const nodes: GraphNode[] = [];

    for (const n of data.nodes) {
      // Skip old colon-format dupes
      if (n.id.includes(':') && !n.id.startsWith('file:') && !n.id.startsWith('ticket:')) {
        const slashVersion = n.id.replace(':', '/').replace(/ /g, '-');
        if (data.nodes.some(o =>
          o.id === slashVersion ||
          o.id === slashVersion.replace('product/', 'products/').replace('concept/', 'concepts/')
        )) continue;
      }
      if (n.id === 'file:...') continue;

      const label = n.id.split('/').pop()!.replace(/-/g, ' ');
      if (seenLabels.has(label)) continue;
      seenLabels.add(label);
      nodes.push({ ...n, label });
    }

    const nodeIds = new Set(nodes.map(n => n.id));
    const edges = data.edges.filter(e =>
      nodeIds.has(e.source) && nodeIds.has(e.target) && e.source !== e.target
    );

    return { nodes, edges };
  }

  private forceLayout(
    nodes: GraphNode[],
    edges: GraphEdge[],
    nodeMap: Record<string, number>
  ): any[] {
    const positions = nodes.map(() => {
      const phi = Math.acos(2 * Math.random() - 1);
      const theta = Math.random() * Math.PI * 2;
      const r = 25 + Math.random() * 15;
      return new THREE.Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi)
      );
    });

    const velocities = nodes.map(() => new THREE.Vector3());
    const iterations = 300;

    for (let iter = 0; iter < iterations; iter++) {
      const cooling = 1 - iter / iterations;

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const diff = positions[i].clone().sub(positions[j]);
          const dist = Math.max(diff.length(), 0.5);
          const force = diff.normalize().multiplyScalar(800 / (dist * dist));
          velocities[i].add(force);
          velocities[j].sub(force);
        }
      }

      for (const e of edges) {
        const si = nodeMap[e.source], ti = nodeMap[e.target];
        if (si === undefined || ti === undefined) continue;
        const diff = positions[ti].clone().sub(positions[si]);
        const dist = diff.length();
        const force = diff.normalize().multiplyScalar(dist * 0.02 * (e.weight || 1));
        velocities[si].add(force);
        velocities[ti].sub(force);
      }

      for (let i = 0; i < nodes.length; i++) {
        velocities[i].sub(positions[i].clone().multiplyScalar(0.01));
        // Clamp velocity to prevent runaway explosions
        const maxVel = 10;
        if (velocities[i].length() > maxVel) {
          velocities[i].normalize().multiplyScalar(maxVel);
        }
        positions[i].add(velocities[i].clone().multiplyScalar(0.3 * cooling));
        velocities[i].multiplyScalar(0.5);
      }
    }

    // Clamp any nodes that escaped to reasonable bounds
    const maxRadius = 80;
    for (const pos of positions) {
      if (pos.length() > maxRadius) {
        pos.normalize().multiplyScalar(maxRadius);
      }
    }

    return positions;
  }

  // ── Combat system ──────────────────────────────────────────────────

  toggleCombat(): void {
    if (!this.flyMode) return; // combat requires fly mode
    this.combatMode = !this.combatMode;
    if (this.combatMode) {
      this.score = 0;
      this.nodesInfected = 0;
      this.infectedNodes = this.nodeMeshes.map(() => false);
      this.nodesRemaining = this.nodeMeshes.length;
      // Restore all node colors to their original type colors
      this.nodeMeshes.forEach((m, i) => {
        const node = m.userData['node'];
        const origColor = TYPE_COLORS_HEX[node?.type] || TYPE_COLORS_HEX.other;
        m.material.color.setHex(origColor);
        m.material.emissive.setHex(origColor);
        m.material.emissiveIntensity = 0.3;
        m.material.opacity = 0.9;
        m.visible = true;
        if (this.glowMeshes[i]) {
          this.glowMeshes[i].material.color.setHex(origColor);
          this.glowMeshes[i].material.opacity = 0.08;
          this.glowMeshes[i].visible = true;
        }
        if (this.labelSprites[i]) {
          this.labelSprites[i].material.opacity = 0.85;
          this.labelSprites[i].visible = true;
        }
      });
      const now = Date.now() * 0.001;
      this.lastBaddieSpawn = now;
      // Pre-allocate shared geometry/material
      this.projectileGeo = new THREE.SphereGeometry(0.15, 6, 6);
      this.projectileMat = new THREE.MeshBasicMaterial({ color: 0x7aa2f7, transparent: true, opacity: 0.9 });
      this.baddieMaterial = new THREE.MeshPhongMaterial({
        color: 0xf7768e, emissive: 0xf7768e, emissiveIntensity: 0.6,
        transparent: true, opacity: 0.9
      });
      this.lastPowerupSpawn = now;
      this.spreadFireActive = false;
      this.spreadFireTimer = 0;
    } else {
      // Cleanup baddies, projectiles, powerups
      this.projectiles.forEach(p => this.scene.remove(p.mesh));
      this.projectiles = [];
      this.baddies.forEach(b => this.scene.remove(b.mesh));
      this.baddies = [];
      this.explosions.forEach(e => this.scene.remove(e.group));
      this.explosions = [];
      this.powerups.forEach(p => this.scene.remove(p.mesh));
      this.powerups = [];
      this.spreadFireActive = false;
      this.spreadFireTimer = 0;
      // Clear threat overlay
      if (this.threatCanvas) {
        const ctx = this.threatCanvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, this.threatCanvas.width, this.threatCanvas.height);
      }
    }
    this.cdr.detectChanges();
  }

  private shoot(): void {
    if (!this.combatMode || !this.scene) return;
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const spawnPos = this.camera.position.clone().add(fwd.clone().multiplyScalar(2));

    if (this.spreadFireActive) {
      // 3-way spread: center + two angled shots
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
      const spreadAngle = 0.12; // ~7 degrees
      const dirs = [
        fwd.clone(),
        fwd.clone().add(right.clone().multiplyScalar(spreadAngle)).normalize(),
        fwd.clone().add(right.clone().multiplyScalar(-spreadAngle)).normalize()
      ];
      for (const d of dirs) {
        const mat = new THREE.MeshBasicMaterial({ color: 0x9ece6a, transparent: true, opacity: 0.9 });
        const mesh = new THREE.Mesh(this.projectileGeo, mat);
        mesh.position.copy(spawnPos);
        this.scene.add(mesh);
        this.projectiles.push({ mesh, velocity: d.multiplyScalar(this.PROJECTILE_SPEED), age: 0 });
      }
    } else {
      const mat = new THREE.MeshBasicMaterial({ color: 0x7aa2f7, transparent: true, opacity: 0.9 });
      const mesh = new THREE.Mesh(this.projectileGeo, mat);
      mesh.position.copy(spawnPos);
      this.scene.add(mesh);
      this.projectiles.push({ mesh, velocity: fwd.multiplyScalar(this.PROJECTILE_SPEED), age: 0 });
    }
  }

  private spawnBaddie(t: number): void {
    if (!this.combatMode || this.baddies.length >= this.MAX_BADDIES) return;
    if (t - this.lastBaddieSpawn < this.BADDIE_SPAWN_INTERVAL) return;
    if (this.nodesRemaining <= 0) return; // all nodes eaten, game over
    this.lastBaddieSpawn = t;

    // Pick a random non-infected node as target
    const aliveIndices = this.infectedNodes.map((infected, i) => !infected ? i : -1).filter(i => i >= 0);
    if (aliveIndices.length === 0) return;
    const targetIdx = aliveIndices[Math.floor(Math.random() * aliveIndices.length)];

    // Spawn on a sphere just outside the node cloud (nodes are at ~25-40 from origin)
    // Direction: from the target node outward, so the baddie approaches from outside
    const targetPos = this.nodeMeshes[targetIdx].position;
    const outward = targetPos.clone().normalize();
    // Offset perpendicular to add variation
    const perp = new THREE.Vector3(
      (Math.random() - 0.5), (Math.random() - 0.5), (Math.random() - 0.5)
    ).normalize();
    const spawnDir = outward.clone().add(perp.multiplyScalar(0.5)).normalize();
    const pos = targetPos.clone().add(spawnDir.multiplyScalar(100 + Math.random() * 60));

    const geo = new THREE.OctahedronGeometry(1.2, 0);
    const mesh = new THREE.Mesh(geo, this.baddieMaterial.clone());
    mesh.position.copy(pos);
    this.scene.add(mesh);

    this.baddies.push({
      mesh,
      targetIdx,
      speed: 0.10 + Math.random() * 0.08,
      hp: 1
    });
  }

  private spawnPowerup(t: number): void {
    if (!this.combatMode || this.powerups.length >= this.MAX_POWERUPS) return;
    if (t - this.lastPowerupSpawn < this.POWERUP_SPAWN_INTERVAL) return;
    this.lastPowerupSpawn = t;

    // Green diamond that orbits the graph
    const geo = new THREE.OctahedronGeometry(0.8, 0);
    const mat = new THREE.MeshPhongMaterial({
      color: 0x9ece6a, emissive: 0x9ece6a, emissiveIntensity: 0.8,
      transparent: true, opacity: 0.9
    });
    const mesh = new THREE.Mesh(geo, mat);
    const orbitRadius = 30 + Math.random() * 20;
    const orbitAngle = Math.random() * Math.PI * 2;
    const orbitY = (Math.random() - 0.5) * 30;
    mesh.position.set(
      Math.cos(orbitAngle) * orbitRadius,
      orbitY,
      Math.sin(orbitAngle) * orbitRadius
    );
    this.scene.add(mesh);
    this.powerups.push({
      mesh, orbitAngle, orbitRadius,
      orbitSpeed: 0.3 + Math.random() * 0.3,
      orbitY
    });
  }

  private updateThreatIndicators(): void {
    if (!this.combatMode || !this.camera || !this.renderer) return;

    // Lazily init the threat canvas
    if (!this.threatCanvas && this.threatCanvasRef) {
      this.threatCanvas = this.threatCanvasRef.nativeElement;
    }
    if (!this.threatCanvas) return;

    const w = this.renderer.domElement.clientWidth;
    const h = this.renderer.domElement.clientHeight;
    if (this.threatCanvas.width !== w || this.threatCanvas.height !== h) {
      this.threatCanvas.width = w;
      this.threatCanvas.height = h;
    }
    const ctx = this.threatCanvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);

    // ── Mini-radar ──
    const radarRadius = 55;
    const radarX = w - radarRadius - 16;
    const radarY = h - radarRadius - 60; // above the fly HUD
    const radarRange = 200; // world units mapped to radar edge

    // Background circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(radarX, radarY, radarRadius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(10, 10, 20, 0.7)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(122, 162, 247, 0.3)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Range rings
    ctx.beginPath();
    ctx.arc(radarX, radarY, radarRadius * 0.5, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(122, 162, 247, 0.12)';
    ctx.stroke();

    // Crosshair lines
    ctx.beginPath();
    ctx.moveTo(radarX - radarRadius, radarY);
    ctx.lineTo(radarX + radarRadius, radarY);
    ctx.moveTo(radarX, radarY - radarRadius);
    ctx.lineTo(radarX, radarY + radarRadius);
    ctx.strokeStyle = 'rgba(122, 162, 247, 0.08)';
    ctx.stroke();

    // Camera forward direction (for rotating blips relative to view)
    const camFwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const camAngle = Math.atan2(camFwd.x, camFwd.z); // yaw angle

    // Player dot (center)
    ctx.beginPath();
    ctx.arc(radarX, radarY, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#7aa2f7';
    ctx.fill();

    // View cone
    ctx.beginPath();
    ctx.moveTo(radarX, radarY);
    const coneLen = radarRadius * 0.4;
    ctx.lineTo(radarX + Math.sin(0.4) * coneLen, radarY - Math.cos(0.4) * coneLen);
    ctx.moveTo(radarX, radarY);
    ctx.lineTo(radarX - Math.sin(0.4) * coneLen, radarY - Math.cos(0.4) * coneLen);
    ctx.strokeStyle = 'rgba(122, 162, 247, 0.25)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Healthy node blips (small blue dots)
    for (let i = 0; i < this.nodeMeshes.length; i++) {
      if (this.infectedNodes[i]) continue;
      const nodePos = this.nodeMeshes[i].position;
      const relX = nodePos.x - this.camera.position.x;
      const relZ = nodePos.z - this.camera.position.z;
      // Rotate relative to camera yaw
      const rotX = relX * Math.cos(-camAngle) - relZ * Math.sin(-camAngle);
      const rotZ = relX * Math.sin(-camAngle) + relZ * Math.cos(-camAngle);
      const rx = (rotX / radarRange) * radarRadius;
      const ry = (-rotZ / radarRange) * radarRadius;
      if (Math.sqrt(rx * rx + ry * ry) > radarRadius - 2) continue;
      ctx.beginPath();
      ctx.arc(radarX + rx, radarY + ry, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(122, 162, 247, 0.4)';
      ctx.fill();
    }

    // Infected node blips (dim red)
    for (let i = 0; i < this.nodeMeshes.length; i++) {
      if (!this.infectedNodes[i]) continue;
      const nodePos = this.nodeMeshes[i].position;
      const relX = nodePos.x - this.camera.position.x;
      const relZ = nodePos.z - this.camera.position.z;
      const rotX = relX * Math.cos(-camAngle) - relZ * Math.sin(-camAngle);
      const rotZ = relX * Math.sin(-camAngle) + relZ * Math.cos(-camAngle);
      const rx = (rotX / radarRange) * radarRadius;
      const ry = (-rotZ / radarRange) * radarRadius;
      if (Math.sqrt(rx * rx + ry * ry) > radarRadius - 2) continue;
      ctx.beginPath();
      ctx.arc(radarX + rx, radarY + ry, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(247, 118, 142, 0.3)';
      ctx.fill();
    }

    // Baddie blips (bright red, pulsing)
    const pulse = 0.6 + Math.sin(Date.now() * 0.006) * 0.4;
    for (const b of this.baddies) {
      const relX = b.mesh.position.x - this.camera.position.x;
      const relZ = b.mesh.position.z - this.camera.position.z;
      const rotX = relX * Math.cos(-camAngle) - relZ * Math.sin(-camAngle);
      const rotZ = relX * Math.sin(-camAngle) + relZ * Math.cos(-camAngle);
      const rx = (rotX / radarRange) * radarRadius;
      const ry = (-rotZ / radarRange) * radarRadius;
      const rdist = Math.sqrt(rx * rx + ry * ry);
      // Clamp to radar edge
      const clampR = Math.min(rdist, radarRadius - 3);
      const bx = rdist > 0 ? radarX + (rx / rdist) * clampR : radarX;
      const by = rdist > 0 ? radarY + (ry / rdist) * clampR : radarY;
      ctx.beginPath();
      ctx.arc(bx, by, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(247, 118, 142, ${pulse})`;
      ctx.fill();
    }

    // Powerup blips (green)
    for (const p of this.powerups) {
      const relX = p.mesh.position.x - this.camera.position.x;
      const relZ = p.mesh.position.z - this.camera.position.z;
      const rotX = relX * Math.cos(-camAngle) - relZ * Math.sin(-camAngle);
      const rotZ = relX * Math.sin(-camAngle) + relZ * Math.cos(-camAngle);
      const rx = (rotX / radarRange) * radarRadius;
      const ry = (-rotZ / radarRange) * radarRadius;
      const rdist = Math.sqrt(rx * rx + ry * ry);
      const clampR = Math.min(rdist, radarRadius - 3);
      const px = rdist > 0 ? radarX + (rx / rdist) * clampR : radarX;
      const py = rdist > 0 ? radarY + (ry / rdist) * clampR : radarY;
      ctx.beginPath();
      // Diamond shape
      ctx.moveTo(px, py - 3);
      ctx.lineTo(px + 2, py);
      ctx.lineTo(px, py + 3);
      ctx.lineTo(px - 2, py);
      ctx.closePath();
      ctx.fillStyle = 'rgba(158, 206, 106, 0.7)';
      ctx.fill();
    }

    ctx.restore();
  }

  private updateCombat(t: number): void {
    if (!this.combatMode) return;

    this.spawnBaddie(t);
    this.spawnPowerup(t);

    // Update spread fire timer
    if (this.spreadFireActive) {
      this.spreadFireTimer -= 1 / 60; // ~60fps
      if (this.spreadFireTimer <= 0) {
        this.spreadFireActive = false;
        this.spreadFireTimer = 0;
      }
    }

    // Update powerups — orbit and spin
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const p = this.powerups[i];
      p.orbitAngle += p.orbitSpeed * 0.016; // ~60fps
      p.mesh.position.set(
        Math.cos(p.orbitAngle) * p.orbitRadius,
        p.orbitY + Math.sin(t * 2 + i) * 3,
        Math.sin(p.orbitAngle) * p.orbitRadius
      );
      p.mesh.rotation.x += 0.04;
      p.mesh.rotation.y += 0.06;
      p.mesh.material.emissiveIntensity = 0.5 + Math.sin(t * 3 + i) * 0.4;
    }

    // Update projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.mesh.position.add(p.velocity);
      p.age++;

      // Check hits against baddies
      let hit = false;
      for (let j = this.baddies.length - 1; j >= 0; j--) {
        const b = this.baddies[j];
        if (p.mesh.position.distanceTo(b.mesh.position) < this.BADDIE_HIT_RADIUS) {
          // Destroy baddie
          this.spawnExplosion(b.mesh.position, 0xf7768e);
          this.scene.remove(b.mesh);
          b.mesh.geometry.dispose();
          b.mesh.material.dispose();
          this.baddies.splice(j, 1);
          this.score++;
          hit = true;
          break;
        }
      }

      // Check hits against powerups
      if (!hit) {
        for (let j = this.powerups.length - 1; j >= 0; j--) {
          const pu = this.powerups[j];
          if (p.mesh.position.distanceTo(pu.mesh.position) < this.POWERUP_HIT_RADIUS) {
            // Collect powerup — activate spread fire
            this.spawnExplosion(pu.mesh.position, 0x9ece6a);
            this.scene.remove(pu.mesh);
            pu.mesh.geometry.dispose();
            pu.mesh.material.dispose();
            this.powerups.splice(j, 1);
            this.spreadFireActive = true;
            this.spreadFireTimer = this.SPREAD_FIRE_DURATION;
            hit = true;
            break;
          }
        }
      }

      if (hit || p.age > this.PROJECTILE_LIFETIME) {
        this.scene.remove(p.mesh);
        this.projectiles.splice(i, 1);
      }
    }

    // Update baddies — move toward target, spin
    for (let i = this.baddies.length - 1; i >= 0; i--) {
      const b = this.baddies[i];
      const targetPos = this.nodeMeshes[b.targetIdx]?.position;

      // If target node was already infected, retarget to another healthy node
      if (this.infectedNodes[b.targetIdx]) {
        const aliveIndices = this.infectedNodes.map((infected, idx) => !infected ? idx : -1).filter(idx => idx >= 0);
        if (aliveIndices.length === 0) {
          this.scene.remove(b.mesh);
          b.mesh.geometry.dispose();
          b.mesh.material.dispose();
          this.baddies.splice(i, 1);
          continue;
        }
        b.targetIdx = aliveIndices[Math.floor(Math.random() * aliveIndices.length)];
        // Skip infection check this frame — just start moving toward new target
        const newNodePos = this.nodeMeshes[b.targetIdx].position;
        const newDir = newNodePos.clone().sub(b.mesh.position).normalize().multiplyScalar(b.speed);
        b.mesh.position.add(newDir);
        b.mesh.rotation.x += 0.03;
        b.mesh.rotation.y += 0.02;
        b.mesh.material.emissiveIntensity = 0.4 + Math.sin(t * 4 + i) * 0.3;
        continue;
      }

      const nodePos = this.nodeMeshes[b.targetIdx].position;
      const dir = nodePos.clone().sub(b.mesh.position);
      const dist = dir.length();

      // Spin menacingly
      b.mesh.rotation.x += 0.03;
      b.mesh.rotation.y += 0.02;

      // Pulsing glow
      b.mesh.material.emissiveIntensity = 0.4 + Math.sin(t * 4 + i) * 0.3;

      if (dist < this.NODE_HIT_RADIUS) {
        // Baddie infects the node — turn it red
        const ni = b.targetIdx;
        this.spawnExplosion(nodePos, 0xf7768e);

        // Infect: change node + glow + label to baddie red
        if (this.nodeMeshes[ni]) {
          this.nodeMeshes[ni].material.color.setHex(0xf7768e);
          this.nodeMeshes[ni].material.emissive.setHex(0xf7768e);
          this.nodeMeshes[ni].material.emissiveIntensity = 0.8;
          this.nodeMeshes[ni].material.opacity = 0.95;
        }
        if (this.glowMeshes[ni]) {
          this.glowMeshes[ni].material.color.setHex(0xf7768e);
          this.glowMeshes[ni].material.opacity = 0.25;
        }
        if (this.labelSprites[ni]) {
          this.labelSprites[ni].material.opacity = 0.3; // dim the label
        }

        this.infectedNodes[ni] = true;
        this.nodesInfected++;
        this.nodesRemaining = this.infectedNodes.filter(inf => !inf).length;

        // Remove the baddie
        this.scene.remove(b.mesh);
        b.mesh.geometry.dispose();
        b.mesh.material.dispose();
        this.baddies.splice(i, 1);
      } else {
        dir.normalize().multiplyScalar(b.speed);
        b.mesh.position.add(dir);
      }
    }

    // Update explosions
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      const e = this.explosions[i];
      e.age++;
      const progress = e.age / 30; // 30 frames = ~0.5s
      e.particles.forEach((p: any) => {
        p.mesh.position.add(p.velocity);
        p.velocity.multiplyScalar(0.92);
        p.mesh.material.opacity = Math.max(0, 1 - progress);
        p.mesh.scale.setScalar(1 - progress * 0.5);
      });
      if (e.age > 30) {
        this.scene.remove(e.group);
        e.group.traverse((obj: any) => {
          if (obj.geometry?.dispose) obj.geometry.dispose();
          if (obj.material?.dispose) obj.material.dispose();
        });
        this.explosions.splice(i, 1);
      }
    }

    // Threat direction indicators
    this.updateThreatIndicators();
  }

  private spawnExplosion(position: any, color: number): void {
    const group = new THREE.Group();
    const particles: { mesh: any; velocity: any }[] = [];
    const count = 12;
    for (let i = 0; i < count; i++) {
      const geo = new THREE.SphereGeometry(0.15, 4, 4);
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(position);
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 1.5
      );
      group.add(mesh);
      particles.push({ mesh, velocity: vel });
    }
    this.scene.add(group);
    this.explosions.push({ particles, age: 0, group });
  }

  private teardownScene(): void {
    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
    if (this.resizeObs) {
      this.resizeObs.disconnect();
      this.resizeObs = null;
    }
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    if (this.keyupHandler) {
      window.removeEventListener('keyup', this.keyupHandler);
      this.keyupHandler = null;
    }
    if (this.mouseMoveHandler) {
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }
    if (this.pointerLockHandler) {
      document.removeEventListener('pointerlockchange', this.pointerLockHandler);
      this.pointerLockHandler = null;
    }
    if (this.renderer?.domElement && this.canvasClickHandler) {
      this.renderer.domElement.removeEventListener('click', this.canvasClickHandler);
      this.canvasClickHandler = null;
    }
    if (this.renderer?.domElement && this.canvasHoverHandler) {
      this.renderer.domElement.removeEventListener('mousemove', this.canvasHoverHandler);
      this.canvasHoverHandler = null;
    }
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    this.controls?.dispose?.();
    if (this.scene) {
      this.scene.traverse((obj: any) => {
        if (obj.geometry?.dispose) obj.geometry.dispose();
        const material = obj.material;
        if (Array.isArray(material)) {
          material.forEach((m: any) => {
            if (m.map?.dispose) m.map.dispose();
            if (m.dispose) m.dispose();
          });
        } else if (material) {
          if (material.map?.dispose) material.map.dispose();
          if (material.dispose) material.dispose();
        }
      });
    }
    this.renderer?.dispose?.();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.nodeMeshes = [];
    this.glowMeshes = [];
    this.labelSprites = [];
    this.flyMode = false;
    this.flySpeed = 0;
    this.flyKeys = {};
    this.flyVelocity = null;
    this.flyEuler = null;
    this.savedCameraPos = null;
    this.savedTarget = null;
    this.combatMode = false;
    this.score = 0;
    this.nodesInfected = 0;
    this.projectiles = [];
    this.baddies = [];
    this.explosions = [];
  }

  // ── Message navigation helpers ──────────────────────────────────

  formatMsgRef(ref: string): string {
    // ref format: "channel-name/YYYY-MM-DD/NNN_out_agent.md"
    const parts = ref.split('/');
    if (parts.length >= 3) {
      const channel = parts[0];
      const date = parts[1];
      const file = parts[2].replace('.md', '').replace(/_/g, ' ');
      return `#${channel} · ${date} · ${file}`;
    }
    return ref;
  }

  navigateToMessage(ref: string): void {
    if (this.messagePreview?.ref === ref && !this.messagePreview.loading) {
      this.messagePreview = null;
      return;
    }
    this.messagePreview = { ref, content: '', loading: true };
    this.cdr.detectChanges();

    const url = `${ConfigService.muxUrl}/knowledge-graph/message?ref=${encodeURIComponent(ref)}&team=${encodeURIComponent(this.team)}`;
    this.http.get<{ content: string | null }>(url).subscribe({
      next: (res) => {
        this.messagePreview = { ref, content: res.content || '(empty)', loading: false };
        this.cdr.detectChanges();
      },
      error: () => {
        this.messagePreview = { ref, content: '(failed to load message)', loading: false };
        this.cdr.detectChanges();
      }
    });
  }

  closePreview(): void {
    this.messagePreview = null;
    this.cdr.detectChanges();
  }

  // --- Search ---

  onSearchInput(): void {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) {
      this.searchResults = [];
      this.searchOpen = false;
      this.restoreNodeOpacity();
      return;
    }
    this.searchResults = this.nodeMeshes
      .map(m => m.userData['node'] as GraphNode)
      .filter(n => n.label.toLowerCase().includes(q))
      .sort((a, b) => {
        // Exact prefix match first, then by reference count
        const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return b.count - a.count;
      })
      .slice(0, 8);
    this.searchActiveIndex = 0;
    this.searchOpen = this.searchResults.length > 0;
    this.dimNonMatching(this.searchResults);
    this.cdr.detectChanges();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (!this.searchOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.searchActiveIndex = Math.min(this.searchActiveIndex + 1, this.searchResults.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.searchActiveIndex = Math.max(this.searchActiveIndex - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.searchResults[this.searchActiveIndex]) {
        this.selectSearchResult(this.searchResults[this.searchActiveIndex]);
      }
    } else if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  selectSearchResult(node: GraphNode): void {
    this.selectedNode = node;
    this.searchOpen = false;
    this.searchQuery = node.label;
    this.controls.autoRotate = false;

    // Find the mesh for this node and fly camera to it
    const mesh = this.nodeMeshes.find(m => m.userData['node']?.id === node.id);
    if (mesh && this.camera && this.controls) {
      const target = mesh.position.clone();
      const cameraOffset = target.clone().add(
        this.camera.position.clone().sub(this.controls.target).normalize().multiplyScalar(25)
      );
      this.animateCamera(cameraOffset, target, 800);
    }

    this.restoreNodeOpacity();
    this.cdr.detectChanges();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.searchOpen = false;
    this.searchActiveIndex = 0;
    this.restoreNodeOpacity();
    this.cdr.detectChanges();
  }

  private animateCamera(targetPos: any, lookAt: any, duration: number): void {
    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const startTime = performance.now();

    const step = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const ease = 1 - Math.pow(1 - t, 3);

      this.camera.position.lerpVectors(startPos, targetPos, ease);
      this.controls.target.lerpVectors(startTarget, lookAt, ease);
      this.controls.update();

      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private dimNonMatching(matches: GraphNode[]): void {
    const matchIds = new Set(matches.map(n => n.id));
    this.nodeMeshes.forEach((mesh, i) => {
      const node = mesh.userData['node'] as GraphNode;
      const isMatch = matchIds.has(node.id);
      mesh.material.opacity = isMatch ? 0.95 : 0.15;
      if (this.glowMeshes[i]) {
        this.glowMeshes[i].material.opacity = isMatch ? 0.15 : 0.02;
      }
      if (this.labelSprites[i]) {
        this.labelSprites[i].material.opacity = isMatch ? 1.0 : 0.1;
      }
    });
  }

  private restoreNodeOpacity(): void {
    this.nodeMeshes.forEach((mesh, i) => {
      mesh.material.opacity = 0.9;
      if (this.glowMeshes[i]) {
        this.glowMeshes[i].material.opacity = this.glowMeshes[i].userData['baseOpacity'] || 0.08;
      }
      if (this.labelSprites[i]) {
        this.labelSprites[i].material.opacity = 0.85;
      }
    });
  }

  toggleTypeFilter(type: string): void {
    if (this.activeTypeFilter === type) {
      this.activeTypeFilter = null;
      this.restoreNodeOpacity();
    } else {
      this.activeTypeFilter = type;
      this.nodeMeshes.forEach((mesh, i) => {
        const node = mesh.userData['node'] as GraphNode;
        const isMatch = node.type === type;
        mesh.material.opacity = isMatch ? 0.95 : 0.08;
        if (this.glowMeshes[i]) {
          this.glowMeshes[i].material.opacity = isMatch ? 0.15 : 0.01;
        }
        if (this.labelSprites[i]) {
          this.labelSprites[i].material.opacity = isMatch ? 1.0 : 0.05;
        }
      });
    }
    this.cdr.detectChanges();
  }
}
